// Quietly offer install when the browser supports it. iOS Safari gets a
// "How to add to Home Screen" modal because it never fires beforeinstallprompt.

import { useEffect, useState } from 'react';
import {
  canPromptInstall,
  isIos,
  isStandalone,
  showInstallPrompt,
  subscribeInstallChange,
} from '../lib/pwa';
import styles from './InstallBanner.module.css';

const DISMISSED_KEY = 'prakashsoftware.agent.installDismissed';

export function InstallBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [, rerender] = useState(0);
  const [showIosGuide, setShowIosGuide] = useState(false);
  // Pin the banner on the OPPOSITE side from the embedded widget launcher so
  // they don't overlap (both default to bottom-right).
  const [side, setSide] = useState<'left' | 'right'>('right');

  useEffect(() => subscribeInstallChange(() => rerender((n) => n + 1)), []);

  useEffect(() => {
    function detectSide() {
      const host = document.querySelector('[data-prakash-widget]');
      if (!host) {
        setSide('right'); // no widget on the page → no overlap, keep default
        return;
      }
      const pos = host.getAttribute('data-bubble-position');
      // Widget defaults to bottom-right when unset → banner goes bottom-left.
      setSide(pos === 'bottom-left' ? 'right' : 'left');
    }
    detectSide();
    // The widget sets data-bubble-position after its async config fetch — re-check.
    const t1 = window.setTimeout(detectSide, 900);
    const t2 = window.setTimeout(detectSide, 2500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  if (dismissed || isStandalone()) return null;

  const promptable = canPromptInstall();
  const ios = isIos();
  if (!promptable && !ios) return null;

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* non-fatal */
    }
  }

  async function install() {
    if (promptable) {
      const outcome = await showInstallPrompt();
      if (outcome !== 'dismissed') dismiss();
    } else if (ios) {
      setShowIosGuide(true);
    }
  }

  return (
    <>
      <aside className={[styles.banner, side === 'left' ? styles.bannerLeft : ''].join(' ')} role="status">
        <div className={styles.body}>
          <p className={styles.kicker}>INSTALL</p>
          <p className={styles.title}>
            Make this dashboard a desktop app — open it from your dock, get system notifications.
          </p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={install}>
            {ios ? 'Show me how' : 'Install'}
          </button>
          <button type="button" className={styles.ghost} onClick={dismiss}>
            Not now
          </button>
        </div>
      </aside>
      {showIosGuide ? <IosGuide onClose={() => setShowIosGuide(false)} /> : null}
    </>
  );
}

function IosGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className={styles.modalBg} role="dialog" aria-modal="true" aria-labelledby="ios-title">
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.modalClose}
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <p className={styles.kicker}>INSTALL · iOS Safari</p>
        <h2 id="ios-title" className={styles.modalTitle}>
          Add to Home Screen
        </h2>
        <ol className={styles.steps}>
          <li>
            Tap the <strong>Share</strong> button in the Safari toolbar.
          </li>
          <li>
            Scroll down and choose <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Confirm the name — <em>IN Agent</em> — and tap <strong>Add</strong>.
          </li>
        </ol>
        <p className={styles.modalFootnote}>
          The app launches without browser chrome and can receive push notifications when you’re
          on the app.
        </p>
      </div>
    </div>
  );
}
